import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Calendar, LinkedIn } from "@/components/icons";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import Typography from "@/components/ui/typography";
import config from "@/config";
import { CohortsKeys, getNextCohorts } from "@/features/cohorts/api/cohorts";
import {
  FoundersKeys,
  getFounderCheckoutLink,
  setOwnCohort,
} from "@/features/founders/api/founders";
import EarningsOverview from "@/features/founders/components/EarningsOverview";
import IncomeChart from "@/features/founders/components/IncomeChart";
import StackLevelsWidget from "@/features/founders/components/StackLevelsWidget";
import { useToast } from "@/hooks/use-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { FounderLevel } from "@/types/levels";

export default function Welcome() {
  const { toast } = useToast();
  const { logout } = useAuth0();
  const { user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { data: cohorts, isLoading: isCohortsLoading } = useQuery({
    queryKey: [CohortsKeys.GET_NEXT_COHORTS],
    queryFn: getNextCohorts,
  });

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [requestPaymentPlan, setRequestPaymentPlan] = useState(false);

  const { mutate: mutateSetCohort, isPending: isSettingCohort } = useMutation({
    mutationKey: [FoundersKeys.SET_COHORT],
    mutationFn: (cohortId: string) => setOwnCohort(cohortId),
    onError: () => {
      toast({
        title: "Failed to update cohort",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const showPaymentComplete = useMemo(() => {
    if (!user?.subscriptionActive || !user?.cohort?.startDate) return false;
    try {
      const start = new Date(user.cohort.startDate);
      return start.getTime() > Date.now();
    } catch {
      return false;
    }
  }, [user?.subscriptionActive, user?.cohort?.startDate]);

  const showDownpaymentPaid = useMemo(() => {
    return Boolean(
      user?.isDownpayment &&
        user?.isDownpaymentPaid &&
        !user?.subscriptionActive,
    );
  }, [user?.isDownpayment, user?.isDownpaymentPaid, user?.subscriptionActive]);

  const showLateJoin = useMemo(() => {
    if (user?.subscriptionActive || !user?.cohort?.startDate) return false;
    try {
      const start = new Date(user.cohort.startDate);
      return start.getTime() <= Date.now();
    } catch {
      return false;
    }
  }, [user?.subscriptionActive, user?.cohort?.startDate]);

  const displayedCohorts = useMemo(() => {
    if ((showPaymentComplete || showDownpaymentPaid) && user?.cohort) {
      return [user.cohort];
    }
    if (!cohorts) return [] as typeof cohorts;
    const userCohort = user?.cohort;
    if (userCohort && !cohorts.some((c) => c._id === userCohort._id)) {
      return [userCohort, ...cohorts].slice(0, 3);
    }
    return cohorts;
  }, [cohorts, user, showPaymentComplete, showDownpaymentPaid, showLateJoin]);

  useEffect(() => {
    if (displayedCohorts && displayedCohorts.length > 0) {
      if (showPaymentComplete || showDownpaymentPaid) {
        setSelectedIndex(0);
      } else {
        const existingIndex = user?.cohort
          ? displayedCohorts.findIndex((c) => c._id === user.cohort!._id)
          : -1;
        setSelectedIndex(existingIndex);
      }
    }
  }, [
    displayedCohorts,
    user,
    showPaymentComplete,
    showDownpaymentPaid,
    showLateJoin,
  ]);

  // Redirect away from Welcome when the user should be in the app
  useEffect(() => {
    if (isLoading || !user) return;
    const cohortStartMs = user.cohort?.startDate
      ? new Date(user.cohort.startDate).getTime()
      : undefined;
    const beforeCohortStart = cohortStartMs
      ? cohortStartMs > Date.now()
      : false;
    const shouldBeOnWelcome =
      !user.subscriptionActive ||
      (user.subscriptionActive && beforeCohortStart);

    if (!shouldBeOnWelcome) {
      navigate("/", { replace: true });
    }
  }, [user, isLoading, navigate]);

  console.log(config.env);

  // Mock data for the blurred preview
  const mockLevelProgress = {
    currentLevel: FounderLevel.STACK_CERTIFIED,
    nextLevel: FounderLevel.STACK_PRO,
    milestones: [
      {
        id: "revenue-1k",
        title: "Generate $1,000 in revenue",
        completed: false,
        progress: 250,
        total: 1000,
        isAutomatic: true,
      },
      {
        id: "complete-training",
        title: "Complete Stack Certified training",
        completed: true,
        isAutomatic: false,
      },
      {
        id: "first-client",
        title: "Secure your first client",
        completed: false,
        isAutomatic: false,
      },
    ],
    levelUnlocked: true,
  };

  const mockEarnings = {
    currentMonth: 250,
    lastMonth: 180,
    ytd: 1250,
    monthlyBreakdown: [
      {
        month: "January",
        year: "2024",
        timestamp: Date.now() - 86400000 * 30,
        earnings: 180,
      },
      {
        month: "February",
        year: "2024",
        timestamp: Date.now(),
        earnings: 250,
      },
    ],
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in">
        {/* Cohort selection popup */}
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            // Prevent closing
            if (!open) setIsDialogOpen(true);
          }}
        >
          <DialogContent
            hideClose
            className="sm:max-w-2xl rounded-xl p-4 sm:p-8 max-w-[95vw]"
          >
            <div className="space-y-8">
              {(config.env === "development" || config.env === "staging") && (
                <button
                  type="button"
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                  className="absolute right-4 top-4 text-xs underline text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded"
                  aria-label="Log out"
                >
                  Log out
                </button>
              )}
              {showPaymentComplete ? (
                <div className="text-center space-y-2 px-1">
                  <Typography size="h2">Payment Completed!</Typography>
                  <Typography size="b2" className="text-muted-foreground">
                    You will receive access to Stack's AI Consulting platform on{" "}
                    <Typography
                      size="b2"
                      as="span"
                      className="font-semibold text-foreground"
                    >
                      {user?.cohort?.startDate
                        ? formatInTimeZone(
                            user.cohort.startDate,
                            "UTC",
                            "MMM do, yyyy",
                          )
                        : "your cohort start date"}
                    </Typography>
                    . We're excited to kick-off!
                  </Typography>
                </div>
              ) : showDownpaymentPaid ? (
                <div className="text-center space-y-2 px-1">
                  <Typography size="h2">Downpayment is Secured!</Typography>
                  <Typography size="b2" className="text-muted-foreground">
                    Please complete the full payment to get access to Stack's
                    platform on{" "}
                    <Typography
                      size="b2"
                      as="span"
                      className="font-semibold text-foreground"
                    >
                      {user?.cohort?.startDate
                        ? formatInTimeZone(
                            user.cohort.startDate,
                            "UTC",
                            "MMM do, yyyy",
                          )
                        : "your cohort start date"}
                    </Typography>
                  </Typography>
                </div>
              ) : showLateJoin ? (
                <div className="text-center space-y-2 px-1">
                  <Typography size="h2">
                    {user?.firstName
                      ? `${user.firstName} - You're In!`
                      : "You're In!"}
                  </Typography>
                  <Typography size="b2" className="text-muted-foreground">
                    Your cohort started on{" "}
                    {user?.cohort?.startDate
                      ? formatInTimeZone(
                          user.cohort.startDate,
                          "UTC",
                          "MMM do, yyyy",
                        )
                      : "your cohort start date"}
                    . Choose your preferred cohort and complete payment to
                    unlock access.
                  </Typography>
                </div>
              ) : (
                <div className="px-1">
                  <Typography size="h2" className="mb-2">
                    {user?.firstName
                      ? `${user.firstName}, you're in!`
                      : "You're in!"}
                  </Typography>
                  <Typography size="b2" className="text-muted-foreground">
                    Choose your cohort start date to secure your spot
                  </Typography>
                </div>
              )}

              {!showDownpaymentPaid && !showPaymentComplete && (
                <div>
                  <div className="space-y-2">
                    {isCohortsLoading ? (
                      <div className="flex gap-2">
                        <Skeleton className="h-9 w-36" />
                        <Skeleton className="h-9 w-36" />
                        <Skeleton className="h-9 w-36" />
                      </div>
                    ) : (
                      <>
                        <ToggleGroup
                          type="single"
                          value={
                            selectedIndex >= 0 ? selectedIndex.toString() : ""
                          }
                          onValueChange={(value) => {
                            if (
                              !value ||
                              isSettingCohort ||
                              showPaymentComplete ||
                              showDownpaymentPaid
                            )
                              return;
                            const newIndex = parseInt(value);
                            setSelectedIndex(newIndex);
                            const selected = cohorts?.[newIndex];
                            if (selected?._id) {
                              mutateSetCohort(selected._id);
                            }
                          }}
                          className="gap-3 flex-nowrap justify-center overflow-x-auto no-scrollbar px-1"
                        >
                          {displayedCohorts.map((cohort, index) => {
                            const date = new Date(cohort.startDate);
                            return (
                              <ToggleGroupItem
                                key={cohort._id}
                                value={index.toString()}
                                disabled={
                                  isSettingCohort ||
                                  showPaymentComplete ||
                                  showDownpaymentPaid
                                }
                                className="h-10 px-3 sm:px-4 text-sm sm:text-base font-semibold rounded-lg border border-[#e8e8e8] bg-transparent text-[#d1d1d2] data-[state=on]:bg-[#f9f9f9] data-[state=on]:text-[#1e1e24] data-[state=on]:border-[#d1d1d2] disabled:opacity-60 shrink-0"
                              >
                                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
                                  {format(date, "MMM do, yyyy")}
                                </span>
                              </ToggleGroupItem>
                            );
                          })}
                        </ToggleGroup>
                        {!showPaymentComplete &&
                          !showDownpaymentPaid &&
                          selectedIndex === 0 && (
                            <div className="flex justify-center">
                              <Typography
                                size="l4"
                                className="text-amber-600 font-medium"
                              >
                                Last spots left
                              </Typography>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="text-center">
                <div className="mx-auto flex -space-x-2 justify-center">
                  {[
                    "/founders/1.png",
                    "/founders/2.jpeg",
                    "/founders/3.png",
                    "/founders/4.jpeg",
                    "/founders/5.jpeg",
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Founder ${i + 1}`}
                      className="h-7 w-7 sm:h-8 sm:w-8 rounded-full border-2 border-background object-cover"
                      loading="lazy"
                    />
                  ))}
                </div>
                <Typography size="l4" className="mt-2 text-muted-foreground">
                  Trusted by 100+ founders from
                </Typography>
              </div>

              <div className="flex flex-col items-center gap-6">
                <div className="opacity-90 flex items-center gap-3 sm:gap-4 flex-wrap justify-center">
                  <img
                    src="/logos/google.png"
                    alt="Google"
                    className="h-3 sm:h-4 w-auto shrink-0"
                  />
                  <img
                    src="/logos/meta.png"
                    alt="Meta"
                    className="h-3 sm:h-4 w-auto shrink-0"
                  />
                  <img
                    src="/logos/salesforce.png"
                    alt="Salesforce"
                    className="h-3 sm:h-4 w-auto shrink-0"
                  />
                  <img
                    src="/logos/harvard.png"
                    alt="Harvard"
                    className="h-3 sm:h-4 w-auto shrink-0"
                  />
                  <img
                    src="/logos/mit.png"
                    alt="MIT"
                    className="h-3 sm:h-4 w-auto shrink-0"
                  />
                </div>
                {(showDownpaymentPaid ||
                  showLateJoin ||
                  !user?.subscriptionActive) && (
                  <div className="flex justify-between items-center w-full gap-3">
                    <a
                      href="https://www.linkedin.com/company/hellostack"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedIn
                        className="h-6 w-6 cursor-pointer"
                        color="#a4a4a4"
                      />
                    </a>
                    <Button
                      size="lg"
                      disabled={
                        (selectedIndex < 0 &&
                          !showDownpaymentPaid &&
                          !showLateJoin) ||
                        isSettingCohort
                      }
                      onClick={async () => {
                        try {
                          const { url } =
                            await getFounderCheckoutLink(requestPaymentPlan);
                          if (url) {
                            window.location.href = url;
                          }
                        } catch (e) {
                          toast({
                            title: "Failed to start checkout",
                            description: "Please try again.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <Typography size="l2" as="span" className="text-white">
                        {showDownpaymentPaid
                          ? "Complete Now"
                          : showLateJoin
                            ? "Get Access"
                            : "Secure your spot"}
                      </Typography>
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Blurred Preview Section */}
        <div className="relative">
          {/* Blur overlay */}
          <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 pointer-events-none rounded-lg" />

          {/* Preview content */}
          <div className="space-y-8 pointer-events-none select-none">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="space-y-1">
                <Typography size="h2">Your Business Hub</Typography>
                <Typography size="b2" className="text-muted-foreground">
                  Your Stack Dashboard
                </Typography>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <StackLevelsWidget
                levelProgress={mockLevelProgress}
                isLoading={false}
                hideStatusBenefitsLink={true}
              />

              <Typography size="sh1">Earnings Overview</Typography>
              <EarningsOverview earnings={mockEarnings} isLoading={false} />

              <IncomeChart earnings={mockEarnings} isLoading={false} />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
